// src/pages/TranslationReviewPage.tsx
import React, { useState, useMemo } from "react";
import enRaw from "../i18n/locales/en.json";
import esRaw from "../i18n/locales/es.json";

// ---------------------------------------------------------------------------
// Flatten nested JSON into dot-separated keys
// e.g. { common: { save: "Save" } } → { "common.save": "Save" }
// ---------------------------------------------------------------------------
function flatten(obj: any, prefix = ""): Record<string, string> {
  const out: Record<string, string> = {};
  for (const k of Object.keys(obj)) {
    const val = obj[k];
    const fullKey = prefix ? `${prefix}.${k}` : k;
    if (val !== null && typeof val === "object" && !Array.isArray(val)) {
      Object.assign(out, flatten(val, fullKey));
    } else {
      out[fullKey] = Array.isArray(val) ? JSON.stringify(val) : String(val ?? "");
    }
  }
  return out;
}

// Top-level section names for accordion grouping
const SECTION_LABELS: Record<string, string> = {
  common: "Common",
  auth: "Auth / Login",
  topnav: "Top Nav",
  sidebar: "Sidebar",
  dashboard: "Dashboard",
  projects: "Projects",
  projectDetail: "Project Detail",
  scope: "Scope",
  expenses: "Expenses",
  paymentsReceived: "Payments Received",
  wages: "Wages",
  subContracts: "Sub-Contracts",
  partnerDisbursements: "Partner Disbursements",
  partnerSettlement: "Partner Settlement",
  clientDecisions: "Client Decisions",
  invoices: "Invoices",
  invoiceDetail: "Invoice Detail",
  invoicePrint: "Invoice Print",
  myCompany: "My Company",
  schedule: "Schedule",
  materials: "Materials",
  resources: "Resources",
  clients: "Clients",
  resetData: "Reset Data",
  adminTenant: "Admin / Tenant",
  quickStart: "Quick Start Guide",
};

// Keys where EN === ES is intentionally correct (universal symbols, brand names,
// loanwords that are identical in Spanish, phone format examples, etc.)
// These are suppressed from the "issues" count — shown with an "OK" indicator instead.
const ACCEPTABLE_SAME: Set<string> = new Set([
  "common.id",
  "common.total",
  "common.no",
  "clients.phonePlaceholder",
  "projects.readyNo",
  "wages.totalLabel",
  "wages.colTotal",
  "wages.noOption",
  "paymentsReceived.totalLabel",
  "paymentsReceived.colId",
  "subContracts.colId",
  "dashboard.pctLabel",
  "partnerDisbursements.totalLabel",
  "partnerDisbursements.colId",
  "invoices.colTotal",
  "invoices.statusFinal",
  "myCompany.logoPlaceholder",
  "myCompany.phonePlaceholder",
  "schedule.area",
  "auth.passwordPlaceholder",
  "topnav.brand",
  "sidebar.brand",
  "partnerSettlement.colTotal",
  "invoiceDetail.taxableNo",
  "invoiceDetail.subtotalLabel",
  "invoiceDetail.totalLabel",
  "materials.materialCol",
  "materials.urlCol",
  "materials.libColMaterial",
  "materials.libColUrl",
  "materials.libColGlobal",
  "materials.globalBadge",
  "adminTenant.tenantsBtn",
  "adminTenant.tenantSelectLabel",
  "adminTenant.tenantsTitle",
  "invoicePrint.colTotal",
]);

type Change = { key: string; original: string; updated: string };

export default function TranslationReviewPage() {
  const enFlat = useMemo(() => flatten(enRaw), []);
  const esFlat = useMemo(() => flatten(esRaw), []);

  // Local edits: key → new ES value
  const [edits, setEdits] = useState<Record<string, string>>({});
  // Which row is being edited
  const [editingKey, setEditingKey] = useState<string | null>(null);
  const [editValue, setEditValue] = useState("");
  // Accordion open state
  const [openSection, setOpenSection] = useState<string | null>(null);
  // Search
  const [search, setSearch] = useState("");

  // Group keys by top-level section
  const sections = useMemo(() => {
    const map: Record<string, string[]> = {};
    for (const key of Object.keys(enFlat)) {
      const section = key.split(".")[0];
      if (!map[section]) map[section] = [];
      map[section].push(key);
    }
    return map;
  }, [enFlat]);

  const sectionKeys = Object.keys(sections).sort((a, b) => {
    const order = Object.keys(SECTION_LABELS);
    const ai = order.indexOf(a);
    const bi = order.indexOf(b);
    if (ai === -1 && bi === -1) return a.localeCompare(b);
    if (ai === -1) return 1;
    if (bi === -1) return -1;
    return ai - bi;
  });

  const startEdit = (key: string) => {
    setEditingKey(key);
    setEditValue(edits[key] ?? esFlat[key] ?? "");
  };

  const cancelEdit = () => {
    setEditingKey(null);
    setEditValue("");
  };

  const saveEdit = (key: string) => {
    const original = esFlat[key] ?? "";
    const trimmed = editValue.trim();
    if (trimmed && trimmed !== original) {
      setEdits((prev) => ({ ...prev, [key]: trimmed }));
    } else if (trimmed === original) {
      // Reverted to original — remove from edits
      setEdits((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
    }
    setEditingKey(null);
    setEditValue("");
  };

  const revertKey = (key: string) => {
    setEdits((prev) => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  const changedCount = Object.keys(edits).length;

  const exportChanges = () => {
    const changes: Change[] = Object.entries(edits).map(([key, updated]) => ({
      key,
      original: esFlat[key] ?? "",
      updated,
    }));
    const payload = {
      exportedAt: new Date().toISOString(),
      totalChanges: changes.length,
      changes,
    };
    const blob = new Blob([JSON.stringify(payload, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `translation-corrections-${new Date().toISOString().slice(0, 10)}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // Filter keys for search
  const filterKeys = (keys: string[]) => {
    if (!search.trim()) return keys;
    const q = search.toLowerCase();
    return keys.filter((k) => {
      const en = enFlat[k] ?? "";
      const es = edits[k] ?? esFlat[k] ?? "";
      return k.toLowerCase().includes(q) || en.toLowerCase().includes(q) || es.toLowerCase().includes(q);
    });
  };

  // Count issues per section: missing ES or EN===ES but NOT in the acceptable whitelist
  const sectionIssues = useMemo(() => {
    const out: Record<string, number> = {};
    for (const [sec, keys] of Object.entries(sections)) {
      out[sec] = keys.filter((k) => {
        if (ACCEPTABLE_SAME.has(k)) return false;
        const en = enFlat[k] ?? "";
        const es = esFlat[k] ?? "";
        return !es || es === en;
      }).length;
    }
    return out;
  }, [sections, enFlat, esFlat]);

  const totalIssues = Object.values(sectionIssues).reduce((a, b) => a + b, 0);

  return (
    <div className="container" style={{ maxWidth: 1100 }}>
      <div className="pageHeader">
        <div>
          <h1 className="pageTitle">Translation Review</h1>
          <p className="pageNote">
            Compare English and Spanish strings. Click any Spanish cell to edit. Export flagged changes to share for updates.
          </p>
        </div>
        <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
          {totalIssues > 0 && (
            <span className="trBadge trBadgeMismatch">{totalIssues} possible issues</span>
          )}
          {changedCount > 0 && (
            <>
              <span className="trBadge trBadgeModified">{changedCount} modified</span>
              <button className="btn btnPrimary" onClick={exportChanges} type="button">
                ⬇ Export Changes
              </button>
            </>
          )}
        </div>
      </div>

      {/* Warning */}
      <div className="trWarning">
        ⚠️ Changes made here are <strong>not saved to the app automatically</strong>. Click <strong>Export Changes</strong> and share the file to have updates applied and redeployed.
      </div>

      {/* Search */}
      <div style={{ marginBottom: 16 }}>
        <input
          className="input"
          placeholder="Search keys, English text, or Spanish text..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{ maxWidth: 480 }}
        />
        {search && (
          <button className="btn btnSecondary" style={{ marginLeft: 8 }} onClick={() => setSearch("")} type="button">
            Clear
          </button>
        )}
      </div>

      {/* Accordion sections */}
      {sectionKeys.map((sec) => {
        const keys = filterKeys(sections[sec] ?? []);
        if (search && keys.length === 0) return null;

        const label = SECTION_LABELS[sec] || sec;
        const issues = sectionIssues[sec] ?? 0;
        const modified = keys.filter((k) => edits[k]).length;
        const isOpen = openSection === sec || !!search;

        return (
          <div key={sec} className="trSection">
            <button
              type="button"
              className="trSectionHeader"
              onClick={() => !search && setOpenSection((p) => (p === sec ? null : sec))}
            >
              <span className="trSectionLeft">
                <span className="trSectionName">{label.toUpperCase()}</span>
                <span className="trSectionCount">{sections[sec].length} keys</span>
                {issues > 0 && <span className="trBadge trBadgeMismatch">{issues} issues</span>}
                {modified > 0 && <span className="trBadge trBadgeModified">{modified} edited</span>}
              </span>
              {!search && <span className="trChevron">{isOpen ? "▲" : "▼"}</span>}
            </button>

            {isOpen && (
              <div className="trTableWrap">
                <table className="trTable">
                  <thead>
                    <tr>
                      <th style={{ width: "26%" }}>Key</th>
                      <th style={{ width: "34%" }}>English</th>
                      <th style={{ width: "34%" }}>Spanish</th>
                      <th style={{ width: "6%" }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {keys.map((key) => {
                      const en = enFlat[key] ?? "";
                      const esOriginal = esFlat[key] ?? "";
                      const esDisplay = edits[key] ?? esOriginal;
                      const isAcceptable = ACCEPTABLE_SAME.has(key);
                      const isMismatch = !isAcceptable && (!esOriginal || esOriginal === en);
                      const isEdited = !!edits[key];
                      const isEditing = editingKey === key;

                      return (
                        <tr
                          key={key}
                          className={`trRow${isMismatch ? " trRowMismatch" : ""}${isEdited ? " trRowEdited" : ""}`}
                        >
                          <td className="trKeyCell">
                            <span className="trKey">{key}</span>
                            {isMismatch && <span className="trBadge trBadgeMismatch" style={{ marginLeft: 4 }}>!</span>}
                            {isAcceptable && !isEdited && <span className="trBadge trBadgeAcceptable" style={{ marginLeft: 4 }}>OK</span>}
                            {isEdited && <span className="trBadge trBadgeModified" style={{ marginLeft: 4 }}>✎</span>}
                          </td>
                          <td className="trEnCell">{en}</td>
                          <td
                            className="trEsCell"
                            onClick={() => !isEditing && startEdit(key)}
                            title="Click to edit"
                          >
                            {isEditing ? (
                              <div className="trEditWrap">
                                <textarea
                                  className="trTextarea"
                                  value={editValue}
                                  onChange={(e) => setEditValue(e.target.value)}
                                  autoFocus
                                  rows={Math.max(2, editValue.split("\n").length)}
                                />
                                <div className="trEditActions">
                                  <button className="btn btnPrimary" style={{ fontSize: 12, padding: "3px 10px" }} onClick={() => saveEdit(key)} type="button">Save</button>
                                  <button className="btn btnSecondary" style={{ fontSize: 12, padding: "3px 10px" }} onClick={cancelEdit} type="button">Cancel</button>
                                </div>
                              </div>
                            ) : (
                              <span className={isEdited ? "trEditedValue" : ""}>{esDisplay || <span style={{ color: "var(--muted)" }}>—</span>}</span>
                            )}
                          </td>
                          <td className="trActionsCell">
                            {isEdited && !isEditing && (
                              <button
                                className="btn btnSecondary"
                                style={{ fontSize: 11, padding: "2px 8px" }}
                                onClick={() => revertKey(key)}
                                title="Revert to original"
                                type="button"
                              >
                                ↺
                              </button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        );
      })}

      <div style={{ height: 48 }} />
    </div>
  );
}
